import { User } from '../types/user';
import { NewsItem, NewsRequest } from '../types/news';
import { Order } from '../types/order';
import { Operation, Mission, OperationDictionary } from '../types/operation';
import {
  DeviceCatalogEntry, DeviceCatalogPayload,
  OperationRecord, OperationRecordListResponse, OperationRecordPayload,
  OperationFieldConfig, OperationFieldConfigListResponse, OperationFieldConfigPayload,
  FieldNode, FieldNodeListResponse, FieldNodePayload, FieldNodeSellPayload, FieldNodeAssignMapPayload, FieldNodeUpdatePayload,
  FieldMissionDevice, FieldMissionDeviceListResponse, CreateFieldMissionDevicePayload, UpdateFieldMissionDevicePayload,
  DeviceMatchingResponse
} from '../types/device';
import { UserCreateRequest, UserUpdateRequest } from '../types/user';
import { API_URL } from '../constants/api';

// Функция для получения токена авторизации
const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

// Базовый URL для API admin
const ADMIN_API_URL = `${API_URL}/api/admin`;

// Общая функция для выполнения запросов
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  // Получаем токен из localStorage
  const token = localStorage.getItem('token');
  
  console.log(`Выполняем запрос: ${options.method || 'GET'} ${url}`);
  console.log('Тело запроса:', options.body);
  
  // Устанавливаем заголовки авторизации
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  
  try {
    // Логируем полный URL и заголовки
    console.log('Полный URL:', url);
    console.log('Заголовки:', headers);
    
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    console.log(`Ответ от сервера: ${response.status} ${response.statusText}`);
    
    // Попытка прочитать текст ответа для логирования
    const responseText = await response.text();
    console.log('Тело ответа:', responseText);
    
    if (!response.ok) {
      console.error('Текст ошибки:', responseText);
      
      let errorData;
      try {
        errorData = JSON.parse(responseText);
        console.error('Данные ошибки:', errorData);
      } catch (e) {
        console.error('Не удалось разобрать JSON ошибки');
      }
      
      throw new Error(errorData?.message || `Ошибка сервера: ${response.status}`);
    }
    
    // Преобразовать ответ обратно в JSON, если это возможно
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.warn('Не удалось разобрать ответ как JSON, возвращаем текст:', responseText);
      return responseText;
    }
    
    console.log('Данные ответа:', data);
    return data;
  } catch (error) {
    console.error('Ошибка при выполнении запроса:', error);
    throw error;
  }
}


// Управление пользователями
export const getUsers = async (page = 1, pageSize = 10, role?: string) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  
  if (role) {
    params.append('role', role);
  }
  
  return fetchWithAuth(`${ADMIN_API_URL}/users?${params.toString()}`);
};

export const getUserById = async (id: string) => {
  return fetchWithAuth(`${ADMIN_API_URL}/users/${id}`);
};

export const updateUser = async (userData: UserUpdateRequest) => {
  console.log('updateUser вызван с данными:', userData);
  
  try {
    console.log('Подготовка данных для отправки:', { request: userData });
    console.log('JSON данных:', JSON.stringify({ request: userData }));
    
    const result = await fetchWithAuth(`${ADMIN_API_URL}/UpdateUser`, {
      method: 'POST',
      body: JSON.stringify({ request: userData })
    });
    
    console.log('Результат updateUser:', result);
    return result;
  } catch (error) {
    console.error('Ошибка в updateUser:', error);
    throw error;
  }
};

export const deleteUser = async (id: string) => {
  return fetchWithAuth(`${ADMIN_API_URL}/users/${id}`, {
    method: 'DELETE'
  });
};

export const createUser = async (userData: UserCreateRequest) => {
  // Убедимся, что статус задан
  const userDataWithStatus = {
    ...userData,
    status: userData.status || 'PENDING'
  };
  
  return fetchWithAuth(`${ADMIN_API_URL}/createuser`, {
    method: 'POST',
    body: JSON.stringify(userDataWithStatus)
  });
};

// Управление новостями
export const getNews = async (page = 1, pageSize = 10, status?: string) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  
  if (status) {
    params.append('status', status);
  }
  
  try {
    const response = await fetchWithAuth(`${ADMIN_API_URL}/news?${params.toString()}`);
    console.log('News data received:', response);
    return response;
  } catch (error) {
    console.error('Error fetching news:', error);
    throw error;
  }
};

export const getNewsById = async (id: string) => {
  try {
    const response = await fetchWithAuth(`${ADMIN_API_URL}/news/${id}`);
    console.log('News details received:', response);
    return response;
  } catch (error) {
    console.error(`Error fetching news ID ${id}:`, error);
    throw error;
  }
};

// Вспомогательная функция для преобразования UPPERCASE строки статуса в PascalCase
function getStatusInPascalCase(status: string): string {
  switch (status.toUpperCase()) {
    case 'DRAFT': return 'Draft';
    case 'PUBLISHED': return 'Published';
    case 'ARCHIVED': return 'Archived';
    default: 
      console.warn(`Неизвестный статус: ${status}, возвращаем Draft`);
      return 'Draft';
  }
}

export const createNews = async (newsData: NewsRequest) => {
  try {
    console.log('Creating news with data:', newsData);
    
    // Format data according to backend expectations
    const formattedData = {
      title: newsData.title,
      content: newsData.content,
      category: newsData.category,
      status: getStatusInPascalCase(newsData.status),
      imageUrl: newsData.imageUrl || null
    };
    
    console.log('Formatted news data:', formattedData);
    
    const response = await fetchWithAuth(`${ADMIN_API_URL}/news`, {
      method: 'POST',
      body: JSON.stringify(formattedData)
    });
    
    console.log('News creation response:', response);
    return response;
  } catch (error) {
    console.error('Error creating news:', error);
    throw error;
  }
};

export const updateNews = async (id: string, newsData: NewsRequest) => {
  try {
    console.log(`Updating news ${id} with data:`, newsData);
    
    // Format data according to backend expectations
    const formattedData = {
      title: newsData.title,
      content: newsData.content,
      category: newsData.category,
      status: getStatusInPascalCase(newsData.status),
      imageUrl: newsData.imageUrl || null
    };
    
    console.log('Formatted news update data:', formattedData);
    
    const response = await fetchWithAuth(`${ADMIN_API_URL}/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(formattedData)
    });
    
    console.log('News update response:', response);
    return response;
  } catch (error) {
    console.error(`Error updating news ${id}:`, error);
    throw error;
  }
};

export const deleteNews = async (id: string) => {
  try {
    console.log(`Deleting news ${id}`);
    const response = await fetchWithAuth(`${ADMIN_API_URL}/news/${id}`, {
      method: 'DELETE'
    });
    console.log('Delete response:', response);
    return response;
  } catch (error) {
    console.error(`Error deleting news ${id}:`, error);
    throw error;
  }
};

// Управление заказами
export const getOrders = async (page = 1, pageSize = 10, status?: string) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  
  if (status) {
    params.append('status', status);
  }
  
  return fetchWithAuth(`${ADMIN_API_URL}/orders?${params.toString()}`);
};

export const getOrderById = async (id: string) => {
  return fetchWithAuth(`${ADMIN_API_URL}/orders/${id}`);
};

export const updateOrderStatus = async (id: string, status: string) => {
  return fetchWithAuth(`${ADMIN_API_URL}/orders/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  });
};

export const deleteOrder = async (id: string) => {
  return fetchWithAuth(`${ADMIN_API_URL}/orders/${id}`, {
    method: 'DELETE'
  });
};

// Управление миссиями (через API продуктов)
export const getMissions = async (page = 1, pageSize = 10, status?: string) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  
  if (status) {
    params.append('status', status);
  }
  
  return fetchWithAuth(`${ADMIN_API_URL}/operations?${params.toString()}`);
};

export const getMissionById = async (id: string) => {
  return fetchWithAuth(`${ADMIN_API_URL}/operations/${id}`);
};

export const createMission = async (missionData: Partial<Mission>) => {
  try {
    console.log('Исходные данные миссии:', missionData);
    
    // Формируем данные в точном соответствии с классом ProductRequest
    const productRequestData = {
      Title: missionData.name || 'Новая миссия',
      Description: missionData.description || 'Описание миссии',
      Price: typeof missionData.price === 'number' ? missionData.price : 0,
      Difficulty: getDifficultyAsString(typeof missionData.difficulty === 'number' ? missionData.difficulty : 2),
      Duration: typeof missionData.duration === 'number' ? missionData.duration : 120,
      PlayerCountMin: typeof missionData.minPlayers === 'number' ? missionData.minPlayers : 6,
      PlayerCountMax: typeof missionData.maxPlayers === 'number' ? missionData.maxPlayers : 12,
      Type: missionData.gameType === 'AIRSOFT' ? "Airsoft" : "Paintball",
      Images: Array.isArray(missionData.images) ? missionData.images : [],
      Videos: Array.isArray(missionData.videos) ? missionData.videos : [],
      Files: Array.isArray(missionData.files) ? missionData.files : [],
      Tags: Array.isArray(missionData.tags) ? missionData.tags : [`MISSION-${missionData.missionId || 'UNKNOWN'}`],
      IsActive: missionData.status === 'ACTIVE',
      WinPoints: typeof missionData.winPoints === 'number' ? missionData.winPoints : 0,
      LosePoints: typeof missionData.losePoints === 'number' ? missionData.losePoints : 0,
      MissionObjectives: Array.isArray(missionData.objectives)
        ? missionData.objectives.map((objective) => ({
            Name: objective.name,
            Count: typeof objective.count === 'number' ? objective.count : 0,
            Team: objective.team === 'alien' ? 'alien' : 'human',
            Type: objective.type || 'count'
          }))
        : []
    };
    
    console.log('Отправка данных создания миссии:', JSON.stringify(productRequestData, null, 2));
    
    const response = await fetchWithAuth(`${ADMIN_API_URL}/operations`, {
      method: 'POST',
      body: JSON.stringify(productRequestData)
    });
    
    console.log('Ответ от сервера при создании миссии:', response);
    return response;
  } catch (error) {
    console.error('Ошибка при создании миссии:', error);
    throw error;
  }
};

export const updateMission = async (id: string, missionData: Partial<Mission>) => {
  try {
    console.log('Исходные данные обновления миссии:', missionData);
    
    // Формируем данные в точном соответствии с классом ProductRequest
    const productRequestData = {
      Title: missionData.name || 'Новая миссия',
      Description: missionData.description || 'Описание миссии',
      Price: typeof missionData.price === 'number' ? missionData.price : 0,
      Difficulty: getDifficultyAsString(typeof missionData.difficulty === 'number' ? missionData.difficulty : 2),
      Duration: typeof missionData.duration === 'number' ? missionData.duration : 120,
      PlayerCountMin: typeof missionData.minPlayers === 'number' ? missionData.minPlayers : 6,
      PlayerCountMax: typeof missionData.maxPlayers === 'number' ? missionData.maxPlayers : 12,
      Type: missionData.gameType === 'AIRSOFT' ? "Airsoft" : "Paintball",
      Images: Array.isArray(missionData.images) ? missionData.images : [],
      Videos: Array.isArray(missionData.videos) ? missionData.videos : [],
      Files: Array.isArray(missionData.files) ? missionData.files : [],
      Tags: Array.isArray(missionData.tags) ? missionData.tags : [`MISSION-${missionData.missionId || 'UNKNOWN'}`],
      IsActive: missionData.status === 'ACTIVE',
      WinPoints: typeof missionData.winPoints === 'number' ? missionData.winPoints : 0,
      LosePoints: typeof missionData.losePoints === 'number' ? missionData.losePoints : 0,
      MissionObjectives: Array.isArray(missionData.objectives)
        ? missionData.objectives.map((objective) => ({
            Name: objective.name,
            Count: typeof objective.count === 'number' ? objective.count : 0,
            Team: objective.team === 'alien' ? 'alien' : 'human',
            Type: objective.type || 'count'
          }))
        : []
    };
    
    console.log('Отправка данных обновления миссии:', JSON.stringify(productRequestData, null, 2));
    
    const response = await fetchWithAuth(`${ADMIN_API_URL}/operations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productRequestData)
    });
    
    console.log('Ответ от сервера при обновлении миссии:', response);
    return response;
  } catch (error) {
    console.error('Ошибка при обновлении миссии:', error);
    throw error;
  }
};

export const deleteMission = async (id: string) => {
  return fetchWithAuth(`${ADMIN_API_URL}/operations/${id}`, {
    method: 'DELETE'
  });
};

// Вспомогательные функции
function getDifficultyFromLevel(level: number): number {
  switch (level) {
    case 1: return 0; // Easy = 0
    case 2: return 1; // Medium = 1
    case 3: return 2; // Hard = 2
    case 4: return 3; // Expert = 3
    default: return 1; // Medium = 1
  }
}

function getDifficultyAsString(level: number): string {
  switch (level) {
    case 1: return "Easy";
    case 2: return "Medium";
    case 3: return "Hard";
    case 4: return "Expert";
    default: return "Medium";
  }
}

function getLevelFromDifficulty(difficulty: string): number {
  switch (difficulty) {
    case 'Easy': return 1;
    case 'Medium': return 2;
    case 'Hard': return 3;
    case 'Expert': return 4;
    default: return 2;
  }
}

// Преобразование продукта в миссию
export function productToMission(product: any): Mission {
  // Более надежное определение типа игры
  let gameType: 'AIRSOFT' | 'PAINTBALL';
  
  // Проверяем разные возможные значения типа игры
  if (typeof product.type === 'string') {
    if (product.type.toLowerCase() === 'paintball') {
      gameType = 'PAINTBALL';
    } else {
      gameType = 'AIRSOFT'; // По умолчанию Airsoft для всех других строковых значений
    }
  } else if (product.type === 1) {
    gameType = 'PAINTBALL';
  } else {
    gameType = 'AIRSOFT'; // По умолчанию Airsoft для всех других случаев
  }
  
  console.log('Преобразование типа игры:', product.type, '→', gameType);
  
  return {
    id: product.id,
    name: product.title,
    description: product.description,
    price: product.price,
    gameType: gameType,
    difficulty: getLevelFromDifficulty(product.difficulty),
    duration: typeof product.duration === 'number' ? product.duration : undefined,
    missionId: product.tags?.find((tag: string) => tag.startsWith('MISSION-'))?.replace('MISSION-', '') || 'UNKNOWN',
    status: product.isActive ? 'ACTIVE' : 'DRAFT',
    winPoints: typeof product.winPoints === 'number' ? product.winPoints : 0,
    losePoints: typeof product.losePoints === 'number' ? product.losePoints : 0,
    objectives: Array.isArray(product.missionObjectives)
      ? product.missionObjectives.map((objective: any) => ({
          id: objective.id,
          name: objective.name,
          count: typeof objective.count === 'number' ? objective.count : 0,
          team: objective.team === 'alien' ? 'alien' : 'human',
          type: (objective.type === 'time' || objective.type === 'points') ? objective.type : 'count',
        }))
      : [],
    isLiveTrackable: product.isLiveTrackable ?? false,
  };
}

// Вспомогательная функция для преобразования строки категории в числовое значение
function getCategoryIndex(category: string): number {
  switch (category.toUpperCase()) {
    case 'GENERAL': return 0;
    case 'UPDATES': return 1;
    case 'EVENTS': return 2;
    case 'ANNOUNCEMENTS': return 3;
    case 'GUIDES': return 4;
    default: return 0; // По умолчанию General
  }
}

// Функция для получения опубликованных новостей (для обычных пользователей)
export const getPublishedNews = async (page = 1, pageSize = 10, category?: string) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
    status: 'Published'
  });
  
  if (category) {
    params.append('category', category);
  }
  
  try {
    // Эта функция использует API не требующее авторизации
    const response = await fetch(`${API_URL}/api/news?${params.toString()}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Ошибка сервера: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching published news:', error);
    throw error;
  }
};

export const getPublishedNewsById = async (id: string) => {
  try {
    // Эта функция использует API не требующее авторизации
    const response = await fetch(`${API_URL}/api/news/${id}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Ошибка сервера: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching published news ID ${id}:`, error);
    throw error;
  }
};

export const createNewsWithFiles = async (newsData: FormData) => {
  try {
    console.log('Creating news with files');
    
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${ADMIN_API_URL}/news`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: newsData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error creating news: ${response.status} ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating news with files:', error);
    throw error;
  }
};

export const updateNewsWithFiles = async (id: string, newsData: FormData) => {
  try {
    console.log(`Updating news ${id} with files`);
    
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${ADMIN_API_URL}/news/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: newsData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error updating news: ${response.status} ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error updating news ${id} with files:`, error);
    throw error;
  }
};

export const createMissionWithFiles = async (missionData: FormData) => {
  try {
    console.log('Creating mission with files');
    
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${ADMIN_API_URL}/operations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: missionData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error creating mission: ${response.status} ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating mission with files:', error);
    throw error;
  }
};

export const updateMissionWithFiles = async (id: string, missionData: FormData) => {
  try {
    console.log(`Updating mission ${id} with files`);
    
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${ADMIN_API_URL}/operations/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: missionData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error updating mission: ${response.status} ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error updating mission ${id} with files:`, error);
    throw error;
  }
};

// Device catalog management
export const getDeviceCatalogEntries = async (includeInactive = false): Promise<DeviceCatalogEntry[]> => {
  const params = new URLSearchParams();
  if (includeInactive) {
    params.append('includeInactive', 'true');
  }
  const url = params.toString()
    ? `${ADMIN_API_URL}/device-catalog?${params.toString()}`
    : `${ADMIN_API_URL}/device-catalog`;
  return fetchWithAuth(url);
};

export const createDeviceCatalogEntry = async (payload: DeviceCatalogPayload, imageFile?: File): Promise<DeviceCatalogEntry> => {
  const token = localStorage.getItem('token');
  const formData = new FormData();

  formData.append('DeviceType', payload.deviceType);
  formData.append('Model', payload.model);
  formData.append('Description', payload.description || '');
  formData.append('IconUrl', payload.iconUrl || '');
  formData.append('IsActive', payload.isActive.toString());

  payload.commands.forEach((cmd, index) => {
    formData.append(`Commands[${index}].Code`, cmd.code);
    formData.append(`Commands[${index}].Name`, cmd.name);
    formData.append(`Commands[${index}].Description`, cmd.description || '');
  });

  if (imageFile) {
    formData.append('ImageFile', imageFile);
  }

  const response = await fetch(`${ADMIN_API_URL}/device-catalog`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to create device');
  }
  return response.json();
};

export const updateDeviceCatalogEntry = async (id: string, payload: DeviceCatalogPayload, imageFile?: File): Promise<DeviceCatalogEntry> => {
  const token = localStorage.getItem('token');
  const formData = new FormData();

  formData.append('DeviceType', payload.deviceType);
  formData.append('Model', payload.model);
  formData.append('Description', payload.description || '');
  formData.append('IconUrl', payload.iconUrl || '');
  formData.append('IsActive', payload.isActive.toString());

  payload.commands.forEach((cmd, index) => {
    formData.append(`Commands[${index}].Code`, cmd.code);
    formData.append(`Commands[${index}].Name`, cmd.name);
    formData.append(`Commands[${index}].Description`, cmd.description || '');
  });

  if (imageFile) {
    formData.append('ImageFile', imageFile);
  }

  const response = await fetch(`${ADMIN_API_URL}/device-catalog/${id}`, {
    method: 'PUT',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Failed to update device');
  }
  return response.json();
};

// Operation records management
export const getOperationRecords = async (page = 1, pageSize = 20): Promise<OperationRecordListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  return fetchWithAuth(`${ADMIN_API_URL}/operation-records?${params.toString()}`);
};

export const getOperationRecordById = async (id: string): Promise<OperationRecord> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-records/${id}`);
};

export const createOperationRecord = async (payload: OperationRecordPayload): Promise<OperationRecord> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-records`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const updateOperationRecord = async (id: string, payload: OperationRecordPayload): Promise<OperationRecord> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-records/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
};

// Получить операции, которых ещё нет в operation_records (доступные для создания)
export const getAvailableOperationsForRecord = async (): Promise<OperationDictionary[]> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-records/available-operations`);
};

// Operation field config management
export const getOperationFieldConfigs = async (page = 1, pageSize = 20): Promise<OperationFieldConfigListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  return fetchWithAuth(`${ADMIN_API_URL}/operation-field-config?${params.toString()}`);
};

export const getOperationFieldConfigById = async (id: string): Promise<OperationFieldConfig> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-field-config/${id}`);
};

export const getOperationFieldConfigsByFieldId = async (fieldId: string): Promise<OperationFieldConfig[]> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-field-config/field/${fieldId}`);
};

export const createOperationFieldConfig = async (payload: OperationFieldConfigPayload): Promise<OperationFieldConfig> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-field-config`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const updateOperationFieldConfig = async (id: string, payload: OperationFieldConfigPayload): Promise<OperationFieldConfig> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-field-config/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
};

export const setOperationFieldConfigStatus = async (id: string, status: string): Promise<OperationFieldConfig> => {
  return fetchWithAuth(`${ADMIN_API_URL}/operation-field-config/${id}/status/${status}`, {
    method: 'PUT'
  });
};

// Field node management
export const getFieldNodes = async (page = 1, pageSize = 20): Promise<FieldNodeListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString()
  });
  return fetchWithAuth(`${ADMIN_API_URL}/field-node?${params.toString()}`);
};

export const getAvailableFieldNodes = async (): Promise<FieldNode[]> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/available`);
};

export const getFieldNodeById = async (id: string): Promise<FieldNode> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/${id}`);
};

export const getFieldNodesByFieldId = async (fieldId: string): Promise<FieldNode[]> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/field/${fieldId}`);
};

export const getFieldNodesByMapId = async (mapId: string): Promise<FieldNode[]> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/map/${mapId}`);
};

export const createFieldNode = async (payload: FieldNodePayload): Promise<FieldNode> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const sellFieldNode = async (id: string, payload: FieldNodeSellPayload): Promise<FieldNode> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/${id}/sell`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
};

export const assignFieldNodeToMap = async (id: string, payload: FieldNodeAssignMapPayload): Promise<FieldNode> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/${id}/assign-map`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
};

export const unassignFieldNodeFromMap = async (id: string): Promise<FieldNode> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/${id}/unassign-map`, {
    method: 'PUT'
  });
};

export const updateFieldNode = async (id: string, payload: FieldNodeUpdatePayload): Promise<FieldNode> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
};

export const activateFieldNode = async (id: string): Promise<FieldNode> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/${id}/activate`, {
    method: 'PUT'
  });
};

export const deactivateFieldNode = async (id: string): Promise<FieldNode> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-node/${id}/deactivate`, {
    method: 'PUT'
  });
};

// Operations dictionary for dropdowns
export const getOperationsDictionary = async (): Promise<OperationDictionary[]> => {
  try {
    // Use public operations API to get list
    const response = await fetch(`${API_URL}/api/operations?page=1&pageSize=100`);
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    const data = await response.json();
    return (data.operations || []).map((p: any) => ({
      id: p.id,
      title: p.title
    }));
  } catch (error) {
    console.error('Error fetching operations dictionary:', error);
    return [];
  }
};

// Alias for backward compatibility
export const getProductsDictionary = getOperationsDictionary;

// Field Mission Device API
export const getFieldMissionDevices = async (
  page: number = 1,
  pageSize: number = 10,
  assignmentStatus?: 'assigned' | 'unassigned'
): Promise<FieldMissionDeviceListResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());
  if (assignmentStatus) params.append('assignmentStatus', assignmentStatus);
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices?${params}`);
};

export const getFieldMissionDeviceById = async (id: string): Promise<FieldMissionDevice> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/${id}`);
};

export const getFieldMissionDevicesByFieldId = async (fieldId: string): Promise<FieldMissionDeviceListResponse> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/by-field/${fieldId}`);
};

export const getUnassignedFieldMissionDevices = async (fieldId: string): Promise<FieldMissionDeviceListResponse> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/by-field/${fieldId}/unassigned`);
};

// Получить все устройства без привязки к полю (fieldId = null) - для продажи
export const getUnassignedToFieldDevices = async (): Promise<FieldMissionDeviceListResponse> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/unassigned-to-field`);
};

export const getFieldMissionDevicesByOfcId = async (ofcId: string): Promise<FieldMissionDeviceListResponse> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/by-ofc/${ofcId}`);
};

// Автоматически подобрать устройства для операции
export const matchDevicesForOperation = async (operationRecordId: string): Promise<DeviceMatchingResponse> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/match-for-operation/${operationRecordId}`);
};

export const createFieldMissionDevice = async (payload: CreateFieldMissionDevicePayload): Promise<FieldMissionDevice> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices`, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const updateFieldMissionDevice = async (id: string, payload: UpdateFieldMissionDevicePayload): Promise<FieldMissionDevice> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
};

export const assignFieldMissionDeviceToOfc = async (fmdId: string, ofcId: string): Promise<void> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/${fmdId}/assign/${ofcId}`, {
    method: 'POST'
  });
};

export const unassignFieldMissionDeviceFromOfc = async (fmdId: string): Promise<void> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/${fmdId}/unassign`, {
    method: 'POST'
  });
};

export const deleteFieldMissionDevice = async (id: string): Promise<void> => {
  return fetchWithAuth(`${ADMIN_API_URL}/field-mission-devices/${id}`, {
    method: 'DELETE'
  });
};
